<template>
  <simple-default-view-component class="core-transaction-detail">
    <template v-slot:header>
      <div class="title">
        <h2>Detalhes da venda</h2>

        <div class="more-details">
          <i class="icon-circle"/>

          <p class="type">{{ methodLabel }}</p>

          <i class="icon-circle"/>

          <p>Data <span>{{ createdDate }}</span></p>
        </div>
      </div>

      <div class="icon-container">
        <i class="icon-close" @click="close"/>
      </div>
    </template>

    <template v-slot:content>
      <div class="content">
        <div class="column detail">
          <div class="details-card-container">
            <div class="details-card">
              <div class="details-option">
                <transaction-status-component :status="data.status" />
              </div>

              <div class="details-header">
                <p>{{ paymentMethodLabel }}</p>

                <a v-if="boletoUrl" :href="boletoUrl" target="_blank">
                  <i class="icon-eye" />

                  Segunda via
                </a>

                <p>{{ $service.formatBrlAmountSymbol(data.amount) }}</p>
              </div>

              <div class="line"></div>

              <div class="items-container">
                <div v-for="(column, index) in columns" :key="index" class="items-content">
                  <div v-for="(row, rowIndex) in column.rows" :key="rowIndex">
                    <div v-if="!!row.value" class="item">
                      <div class="label">
                        <i v-if="row.icon" :class="row.icon"></i>
                        {{ row.label }}
                      </div>

                      <div class="value">
                        {{ row.value }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column table">
          <div class="section-container">
            <h3>Histórico da venda</h3>

            <table-component class="table-component" :table="historyTable" />
          </div>

          <div class="section-container">
            <h3>Transferência do valor líquido da venda</h3>

            <div v-if="hasReceivablesRequest" class="spinner">
              <spinner-component color="#DADADA"/>
            </div>

            <table-component v-else class="table-component" :table="receivablesTable" />
          </div>

          <div v-if="hasSplit" class="section-container">
            <h3>Detalhes do split</h3>

            <div v-if="hasSplitRequest" class="spinner">
              <spinner-component color="#DADADA"/>
            </div>

            <p v-else-if="hasSplitError" class="split-message">Não foi possível carregar o split.</p>

            <template v-else-if="split">
              <div class="split-info">
                <transaction-status-component :status="split.status" />

                <span>Valor original: <b>{{ $service.formatBrlAmountSymbol(split.original_amount) }}</b></span>

                <span>Data do split: <b>{{ formatDate(split.created_at) }}</b></span>
              </div>

              <table-component class="table-component" :table="splitTable" />
            </template>
          </div>
        </div>
      </div>
    </template>
  </simple-default-view-component>
</template>

<script>
  import SimpleDefaultView from '@/components/Views/SimpleDefaultView';
  import TransactionStatus from '@/components/TransactionStatus';
  import Table from '@/components/Table/Table';
  import Spinner from '@/components/Spinner';

  const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
  const DISPLAY_DATE_FORMAT = 'DD/MM/YY - HH:mm';

  export default {
    name: 'CoreTransactionDetail',

    props: {
      // Transaction as returned by the core transactions list
      transaction: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        receivables: [],
        hasReceivablesRequest: true,
        split: null,
        hasSplitRequest: false,
        hasSplitError: false
      };
    },

    components: {
      'simple-default-view-component': SimpleDefaultView,
      'transaction-status-component': TransactionStatus,
      'table-component': Table,
      'spinner-component': Spinner
    },

    created() {
      this.fetchReceivables();

      if (this.hasSplit) this.fetchSplit();
    },

    computed: {
      data() {
        const payment = this.transaction.payment || {};

        return {
          ...this.transaction,
          payment: {
            ...payment,
            credit_card: payment.credit_card || {},
            debit: payment.debit || {},
            pix: payment.pix || {},
            boleto: payment.boleto || {}
          },
          history: this.transaction.history || [],
          split_rules: this.transaction.split_rules || []
        };
      },

      method() {
        return this.$service.toUpperCase(this.data.payment.method);
      },

      isOnlineCreditCard() {
        const pointOfSale = this.data.payment.point_of_sale;

        return !pointOfSale || this.$service.toUpperCase(pointOfSale.entry_mode) === 'MANUALLY_KEYED';
      },

      boletoUrl() {
        return this.method == 'BOLETO' ? this.data.payment.boleto.url : '';
      },

      createdDate() {
        return this.formatDate(this.data.created_at);
      },

      methodLabel() {
        switch (this.method) {
          case 'PIX':
            return 'Pix';
          case 'BOLETO':
            return 'Boleto';
          case 'CREDITCARD':
            return this.isOnlineCreditCard ? 'Online' : 'Maquininha';
          case 'DEBIT':
            return 'Maquininha';
          default:
            return 'Outro';
        }
      },

      paymentMethodLabel() {
        const installments = this.data.payment.credit_card.installments?.count || 1;

        if (this.method == 'CREDITCARD' && installments > 1) {
          return this.$service.formatPaymentMethodName('CREDITCARD_INSTALLMENT', installments);
        }

        return this.$service.formatPaymentMethodName(this.method);
      },

      columns() {
        switch (this.method) {
          case 'CREDITCARD':
            return this.cardColumns(this.data.payment.credit_card);
          case 'DEBIT':
            return this.cardColumns(this.data.payment.debit);
          case 'PIX':
            return this.pixColumns();
          case 'BOLETO':
            return this.boletoColumns();
          default:
            return [];
        }
      },

      hasSplit() {
        return this.data.split_rules.length > 0;
      },

      splitTable() {
        const rows = (this.split?.rules ?? []).map(rule => ({
          name: rule.account?.name || '-',
          document: this.formatDocument(rule.account?.document) || '-',
          division: rule.mode == 'percentage' ? 'Percentual' : 'Valor fixo',
          percentage: rule.mode == 'percentage' ? `${rule.value}%` : '-',
          amount: this.$service.formatBrlAmountSymbol(rule.receivable_gross_amount)
        }));

        if (rows.length) rows[rows.length - 1].noBorder = true;

        return {
          headers: {
            hasBorder: true,
            items: [
              { label: 'Conta', field: 'name', align: 'left' },
              { label: 'Documento', field: 'document', align: 'center' },
              { label: 'Divisão', field: 'division', align: 'center' },
              { label: 'Percentual', field: 'percentage', align: 'center' },
              { label: 'Valor', field: 'amount', align: 'right' }
            ]
          },
          rows
        };
      },

      receivablesTable() {
        return {
          headers: {
            hasBorder: true,
            items: [
              { label: 'Data prevista', field: 'expectedDate', align: 'left' },
              { label: 'Status', field: 'status', align: 'center', isStatus: true },
              { label: 'Parcela', field: 'installment', align: 'center' },
              { label: 'Valor bruto', field: 'grossAmount', align: 'center' },
              { label: 'Taxa de venda', field: 'fee', align: 'center' },
              { label: 'Valor líquido', field: 'amount', align: 'right' }
            ]
          },
          rows: this.formatReceivablesRows(this.receivables)
        };
      },

      historyTable() {
        const rows = this.data.history.map(item => ({
          date: this.formatDate(item.created_at),
          status: 'CUSTOM',
          customStatus: {
            label: this.formatHistoryStatus(item),
            background: '#FFFFFF',
            color: '#333333',
            borderColor: '#DADADA'
          },
          amount: this.$service.formatBrlAmountSymbol(item.amount)
        }));

        rows.push({
          date: 'Status atual',
          status: this.data.status,
          amount: this.$service.formatBrlAmountSymbol(this.data.amount),
          isBold: true,
          noBorder: true
        });

        return {
          headers: {
            hasBorder: true,
            items: [
              { label: 'Data', field: 'date', align: 'left' },
              { label: 'Status', field: 'status', align: 'center', isStatus: true },
              { label: 'Valor', field: 'amount', align: 'right' }
            ]
          },
          rows
        };
      }
    },

    mounted() {
      history.pushState({}, '', this.$router.history.current.path);

      window.onpopstate = () => {
        this.close();
      };
    },

    methods: {
      cardColumns(card) {
        return [
          {
            rows: [
              { icon: 'icon-credit-card', label: card.brand, value: this.formatCardNumber(card) },
              { label: 'Titular do cartão', value: card.holder_name?.trim() ? this.$service.toCapitalize(card.holder_name.trim()) : '' },
              { label: 'ID da transação', value: this.data.id },
              { label: 'Modo', value: this.formatEntryMode() }
            ]
          },

          {
            rows: [
              { label: 'Validade', value: this.formatCardExpiration(card) }
            ]
          }
        ];
      },

      pixColumns() {
        const { pix } = this.data.payment;

        return [
          {
            rows: [
              { label: 'ID da transação', value: this.data.id },
              { label: 'Provedor', value: pix.provider },
              { label: 'Modo', value: this.formatEntryMode() }
            ]
          },

          {
            rows: [
              { label: 'Tipo', value: pix.type ? this.formatPixType(pix.type) : '' }
            ]
          }
        ];
      },

      boletoColumns() {
        const { boleto } = this.data.payment;

        return [
          {
            rows: [
              { label: 'Vencimento', value: this.formatDate(boleto.due_date, 'DD/MM/YY') },
              { label: 'ID da transação', value: this.data.id },
              { label: 'ID de referência', value: this.data.reference_id },
              { label: 'Modo', value: this.formatEntryMode() }
            ]
          },

          {
            rows: [
              { label: 'Nº documento', value: boleto.reference_number }
            ]
          }
        ];
      },

      formatDate(value, format = DISPLAY_DATE_FORMAT) {
        return value ? this.$service.dateFormat(value, DATE_FORMAT, format) : '';
      },

      formatCardNumber(card) {
        if (!card.first_4_digits || !card.last_4_digits) return '';

        return 'Nº do cartão ' + card.first_4_digits + ' **** **** ' + card.last_4_digits;
      },

      formatCardExpiration(card) {
        if (!card.expiration_month || !card.expiration_year) return '';

        return this.$service.dateFormat(card.expiration_month + '/' + card.expiration_year, 'M/YYYY', 'MM/YY');
      },

      formatPixType(type) {
        return this.$service.toUpperCase(type) == 'DYNAMIC' ? 'Dinâmico' : 'Outro';
      },

      formatEntryMode() {
        const entryMode = this.$service.toUpperCase(this.data.payment.point_of_sale?.entry_mode);

        switch (entryMode) {
          case 'CHIP':
            return 'CHIP';
          case 'CONTACTLESS':
          case 'CONTACTLESS_CHIP':
            return 'Aproximação';
          case 'MGSTRIP_FALLBACK':
            return 'Tarja magnética';
          case 'MANUALLY_KEYED':
            return 'Online';
          case 'QR_CODE':
            return 'QR Code';
          case 'BARCODE':
            return this.method == 'PIX' ? 'QR Code' : 'Cód. Barras';
          default:
            return this.method == 'CREDITCARD' ? 'Online' : '';
        }
      },

      formatHistoryStatus(history) {
        const status = this.$service.toUpperCase(history.status);

        switch (this.$service.toUpperCase(history.operation_type)) {
          case 'CREATED':
            return 'Criada';
          case 'AUTHORIZATION':
            return status == 'SUCCEEDED' ? 'Autorizada' : 'Não autorizada';
          case 'PAID':
            return 'Paga';
          case 'EXPIRATION':
            return 'Expirada';
          case 'REFUNDED':
            return 'Estornada';
          case 'VOID':
            return parseFloat(history.amount) == parseFloat(this.data.amount) && this.$service.toUpperCase(this.data.status) == 'CANCELED'
              ? 'Cancelamento Total'
              : 'Cancelamento Parcial';
          default:
            return 'Em análise';
        }
      },

      async fetchReceivables() {
        this.hasReceivablesRequest = true;

        await this.$store.dispatch('getCoreReceivablesTransaction', { transactionId: this.transaction.id })
          .then(res => {
            this.receivables = Array.isArray(res.data) ? res.data : [];
          })
          .catch(err => {
            console.log(err);
          });

        this.hasReceivablesRequest = false;
      },

      async fetchSplit() {
        this.hasSplitRequest = true;
        this.hasSplitError = false;

        await this.$store.dispatch('getCoreSplitRules', { transactionId: this.transaction.id })
          .then(res => {
            this.split = res.data;
          })
          .catch(err => {
            console.log(err);

            this.hasSplitError = true;
          });

        this.hasSplitRequest = false;
      },

      formatDocument(document) {
        const digits = String(document ?? '').replace(/\D/g, '');

        if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');

        return String(document ?? '');
      },

      formatReceivablesRows(receivables) {
        const rows = receivables.map(item => ({
          expectedDate: this.formatDate(item.expected_on),
          status: item.status,
          installment: item.installment ? item.installment : '-',
          grossAmount: this.$service.formatBrlAmountSymbol(item.gross_amount),
          fee: this.$service.formatBrlAmountSymbol(item.gross_amount - item.amount),
          amount: this.$service.formatBrlAmountSymbol(item.amount)
        }));

        rows.sort((a, b) => a.installment - b.installment);
        if (rows.length) rows[rows.length - 1].noBorder = true;

        return rows;
      },

      close() {
        this.$emit('close');
      }
    }
  };
</script>

<style lang="scss" scoped>
  .core-transaction-detail {
    .title {
      display: flex;
      align-items: center;
      margin-right: 1rem;

      h2 {
        color: #333;
      }

      .more-details {
        display: flex;
        align-items: center;

        i {
          font-size: 0.3rem;
          padding: 0 0.5rem;
          color: #DADADA;
        }

        p {
          color: #6D6F6F;
          font-weight: 500;
          font-size: 16px;

          &.type {
            font-size: 14px;
          }

          span {
            color: #333;
          }
        }
      }
    }

    .content {
      padding: 1.5rem;
      display: flex;

      .column {
        &.table {
          width: 100%;
          overflow-x: hidden;

          .section-container {
            overflow-x: hidden;

            .table-component {
              overflow-x: auto;
              overflow-y: hidden;
            }
          }
        }

        .details-card {
          border: 1px solid #EFEFEF;
          padding: 1rem;
          border-radius: 8px;
          width: 420px;

          .details-option {
            margin-bottom: 1.5rem;
          }

          .details-header {
            display: flex;
            padding: 0.7rem 0;
            margin-bottom: 0.3rem;

            p {
              font-weight: 600;
              color: #333;

              &:last-child {
                margin-left: auto;
              }
            }

            a {
              display: flex;
              align-items: center;
              text-decoration: none;
              font-size: 14px;
              margin-left: 0.5rem;
              color: #0052cc;

              i {
                margin-right: 0.2rem;
              }
            }
          }

          .line {
            border-bottom: 1px solid #EFEFEF;
          }

          .items-container {
            display: flex;

            .items-content {
              .item {
                padding: 1rem 0;

                .label {
                  display: flex;
                  align-items: center;
                  font-size: 14px;
                  font-weight: 400;
                  color: #8E8E8E;

                  i {
                    margin-right: 0.5rem;
                  }
                }

                .value {
                  margin-top: 0.3rem;
                  font-size: 14px;
                  color: #333;
                  word-break: break-all;
                }
              }

              &:last-child {
                margin-left: auto;
                text-align: end;

                .label {
                  display: block;
                }
              }
            }
          }
        }

        .section-container {
          h3 {
            color: #333;
            margin-bottom: 0.5rem;
          }

          & + .section-container {
            margin-top: 2rem;
          }
        }

        .spinner {
          display: flex;
          justify-content: center;
          padding: 2rem 0;
        }

        &:last-child {
          margin-left: 1.5rem;
        }
      }
    }
  }

  .core-transaction-detail {
    .content .column.table .section-container {
      .split-info {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.5rem 1.5rem;
        margin-bottom: 0.75rem;
        font-size: 14px;
        color: #8E8E8E;

        b {
          font-weight: 500;
          color: #333;
        }
      }

      .split-message {
        font-size: 14px;
        color: #8E8E8E;
      }
    }
  }
</style>
